import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Layout, 
  Card, 
  Space, 
  Typography, 
  Modal,
  Form,
  InputNumber,
  message,
  Tag,
  Row,
  Col,
  Spin
} from 'antd';
import { 
  EditOutlined,
  CloudServerOutlined,
  DollarCircleOutlined
} from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import apiService from '../services/ApiService';

const { Content, Header } = Layout;
const { Title, Text } = Typography;

const AdminPricingPage = () => {
  const navigate = useNavigate();
  const { user, servers, loadServers } = useApp();
  
  const [loading, setLoading] = useState(false);
  const [editModal, setEditModal] = useState({
    visible: false,
    loading: false,
    server: null
  });
  const [form] = Form.useForm();

  useEffect(() => {
    // Проверяем права администратора
    if (!user || !user.is_admin) {
      navigate('/admin');
      return;
    }

    // Загружаем серверы если они не загружены
    if (!servers.servers || servers.servers.length === 0) {
      loadServers();
    }
  }, [user, navigate, servers, loadServers]);

  useEffect(() => {
    // Настройка BackButton для возврата в админ-панель
    if (window.Telegram?.WebApp?.BackButton) {
      const backButton = window.Telegram.WebApp.BackButton;
      
      const handleBackClick = () => {
        navigate('/admin');
      };

      // Показываем кнопку назад
      backButton.show();
      backButton.onClick(handleBackClick);

      // Cleanup при размонтировании
      return () => {
        backButton.offClick(handleBackClick);
        backButton.hide();
      };
    }
  }, [navigate]);

  const handleEditPricing = (server) => {
    setEditModal({
      visible: true,
      loading: false,
      server: server
    });
    
    // Заполняем форму текущими ценами
    form.setFieldsValue({
      price_week: server.price_week,
      price_month: server.price_month,
      price_year: server.price_year
    });
  };

  const handleSavePricing = async () => {
    try {
      const values = await form.validateFields();
      setEditModal(prev => ({ ...prev, loading: true }));

      await apiService.updateServerPricing(
        editModal.server.id,
        user.user_id,
        {
          price_week: values.price_week,
          price_month: values.price_month,
          price_year: values.price_year
        }
      );

      message.success('Цены успешно обновлены');
      window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      
      // Перезагружаем серверы чтобы получить обновленные данные
      await loadServers();
      
      setEditModal({ visible: false, loading: false, server: null });
      form.resetFields();
    } catch (error) {
      console.error('Ошибка обновления цен:', error);
      message.error('Ошибка при обновлении цен');
      setEditModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleCancelEdit = () => {
    setEditModal({ visible: false, loading: false, server: null });
    form.resetFields();
  };

  const formatPrice = (price) => {
    if (price === 0) {
      return <Tag color="default">Бесплатно</Tag>;
    }
    return `${price} ₽`;
  };

  const renderServerCard = (server) => (
    <Card 
      key={server.id}
      hoverable
      style={{ marginBottom: '12px' }}
      bodyStyle={{ padding: '16px' }}
      onClick={() => handleEditPricing(server)}
    >
      <Row gutter={[16, 8]} align="middle" wrap={false}>
        <Col>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            borderRadius: '8px',
            background: 'var(--ant-color-primary-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <CloudServerOutlined style={{ fontSize: '24px' }} />
          </div>
        </Col>
        <Col flex="auto">
            <Space direction="vertical" size="small" style={{ minWidth: 0, flex: 1 }}>
              <div style={{ 
                fontWeight: 500, 
                fontSize: '16px',
                marginBottom: '4px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {server.name}
              </div>
              <Text style={{ 
                fontSize: '12px',
                fontFamily: 'monospace'
              }} type="secondary">
                {server.ip}
              </Text>
            </Space>
        </Col>
        <Col>
          <Space direction="vertical" size="small" style={{ alignItems: 'flex-end' }}>
              <Text style={{ fontSize: '12px' }} type="secondary">
                Неделя
              </Text>
              <Text style={{ fontWeight: 500 }}>
                {formatPrice(server.price_week)}
              </Text>
              <Text style={{ fontSize: '12px' }} type="secondary">
                Месяц
              </Text>
              <Text style={{ fontWeight: 500 }}>
                {formatPrice(server.price_month)}
              </Text>
              <Text style={{ fontSize: '12px' }} type="secondary">
                Год
              </Text>
              <Text style={{ fontWeight: 500 }}>
                {formatPrice(server.price_year)}
              </Text>
          </Space>
        </Col>
      </Row>
    </Card>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ 
        background: 'transparent', 
        padding: '0 16px',
        borderBottom: '1px solid var(--ant-color-border)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: '100%'
        }}>
          <Title level={4} style={{ margin: 0 }}>
            Тарифы
          </Title>
        </div>
      </Header>

      <Content style={{ padding: '16px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card>
            <Space direction="vertical" size="small">
              <Title level={5} style={{ margin: 0 }}>
                <DollarCircleOutlined style={{ marginRight: '8px', color: '#faad14' }} />
                Настройка тарифов
              </Title>
              <Text type="secondary">
                Настройка цен для каждого сервера в рублях.
              </Text>
            </Space>
          </Card>

          <div>
            {!servers.servers || servers.servers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
              </div>
            ) : (
              servers.servers.map(renderServerCard)
            )}
          </div>
        </Space>
      </Content>

      {/* Модальное окно редактирования цен */}
      <Modal
        title={
          <Space>
            <EditOutlined />
            Редактирование тарифов
          </Space>
        }
        open={editModal.visible}
        onOk={handleSavePricing}
        onCancel={handleCancelEdit}
        confirmLoading={editModal.loading}
        destroyOnClose
        okText="Сохранить"
        cancelText="Отмена"
        width="95%"
        style={{ maxWidth: '400px' }}
      >
        {editModal.server && (
          <Form
            form={form}
            layout="vertical"
            style={{ marginTop: '16px' }}
          >
            <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: '16px' }}>
              <Text type="secondary">
                Настройка тарифов для сервера: <br /><strong>{editModal.server.name}</strong>
              </Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                IP: {editModal.server.ip}
              </Text>
            </Space>

            <Form.Item
              name="price_week"
              label="Цена за неделю (₽)"
              rules={[
                { required: true, message: 'Укажите цену за неделю' },
                { type: 'number', min: 0, message: 'Цена не может быть отрицательной' }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Введите цену за неделю"
                min={0}
                precision={0}
                size="large"
                addonAfter="₽"
                inputMode="numeric"
              />
            </Form.Item>

            <Form.Item
              name="price_month"
              label="Цена за месяц (₽)"
              rules={[
                { required: true, message: 'Укажите цену за месяц' },
                { type: 'number', min: 0, message: 'Цена не может быть отрицательной' }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Введите цену за месяц"
                min={0}
                precision={0}
                size="large"
                addonAfter="₽"
                inputMode="numeric"
              />
            </Form.Item>

            <Form.Item
              name="price_year"
              label="Цена за год (₽)"
              rules={[
                { required: true, message: 'Укажите цену за год' },
                { type: 'number', min: 0, message: 'Цена не может быть отрицательной' }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Введите цену за год"
                min={0}
                precision={0}
                size="large"
                addonAfter="₽"
                inputMode="numeric"
              />
            </Form.Item>

            <div style={{ 
              padding: '12px', 
              background: 'var(--ant-color-fill-alter)', 
              borderRadius: '6px',
              marginTop: '16px'
            }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                💡 Установите цену 0, чтобы сделать тариф бесплатным
              </Text>
            </div>
          </Form>
        )}
      </Modal>
    </Layout>
  );
};

export default AdminPricingPage; 